import { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { IconPaperclip, IconX } from '@shared/ui/icons';
import {
  apiErrorText,
  type FinCounterpartyRow,
  useCreatePaymentRequestMutation,
  useListFinCounterpartiesQuery,
  useListVatRatesQuery,
} from '@shared/api';
import { moneyInput, parseMoney } from '@shared/lib/money';
import { todayIso } from '@shared/lib/localDate';

const REQUEST_CATEGORIES = [
  'materials',
  'fuel',
  'subcontract',
  'payroll',
  'taxes',
  'debt_service',
  'leasing',
  'equipment',
  'overhead',
  'other',
];

/** Реквизиты, без которых заявка не попадёт в файл для банка. */
function hasBankRequisites(cp: FinCounterpartyRow): boolean {
  return /^\d{20}$/.test(cp.account_number?.trim() ?? '') && /^\d{9}$/.test(cp.bik?.trim() ?? '');
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  padding: 16px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;

  &:focus {
    outline: none;
  }
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const ErrorAlert = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  font-size: 13px;
`;

const Textarea = styled.textarea`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  resize: vertical;
  min-height: 60px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldError = styled.div`
  font-size: 11px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
`;

const VatLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const VatButtons = styled.div`
  display: flex;
  gap: 6px;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
`;

const ActiveRegistryBanner = styled.div`
  font-size: 12px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#38bdf8' : '#075985')};
  background: rgba(14, 165, 233, 0.1);
  border: 1px solid ${({ theme }) => (theme.name === 'dark' ? '#38bdf844' : '#07598544')};
  padding: 6px 10px;
  border-radius: 6px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DirectoryBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const RequisitesLine = styled.div<{ $warn?: boolean }>`
  font-size: 12px;
  color: ${({ $warn, theme }) => ($warn ? theme.colors.status.warning : theme.colors.text.secondary)};
`;

const VatToggleBtn = styled.button`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => (theme.name === 'dark' ? '#69D483' : '#15803d')};
  background: rgba(16, 185, 129, 0.1);
  color: ${({ theme }) => (theme.name === 'dark' ? '#69D483' : '#15803d')};
  cursor: pointer;
  font-weight: 600;
`;

const NoVatToggleBtn = styled.button`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font-weight: 600;
`;

interface Props {
  open: boolean;
  onClose: () => void;
  activeRegistryId?: string;
  onSuccess?: () => void;
}

export function CreatePaymentRequestModal({ open, onClose, activeRegistryId, onSuccess }: Props) {
  const [createRequest, { isLoading }] = useCreatePaymentRequestMutation();
  const { data: directory = [] } = useListFinCounterpartiesQuery(undefined, { skip: !open });
  const { data: vatRates } = useListVatRatesQuery(undefined, { skip: !open });

  const [counterpartyName, setCounterpartyName] = useState('');
  const [counterpartyInn, setCounterpartyInn] = useState('');
  // Получатель из справочника: реквизиты для файла банка сервер возьмёт из его карточки
  const [selectedCpId, setSelectedCpId] = useState('');
  const [cpSearch, setCpSearch] = useState('');
  const selectedCp = directory.find((cp) => cp.id === selectedCpId) ?? null;
  const directoryOptions = useMemo(() => {
    const q = cpSearch.trim().toLowerCase();
    const matched = q
      ? directory.filter(
          (cp) => cp.name.toLowerCase().includes(q) || cp.inn.includes(q),
        )
      : directory;
    return matched.slice(0, 200);
  }, [directory, cpSearch]);

  function selectCounterparty(id: string) {
    setSelectedCpId(id);
    const cp = directory.find((row) => row.id === id);
    if (!cp) return;
    setCounterpartyName(cp.name);
    setCounterpartyInn(cp.inn);
    if (REQUEST_CATEGORIES.includes(cp.default_category)) {
      setCategory(cp.default_category);
    }
  }
  const [amount, setAmount] = useState('');
  const [vatAmount, setVatAmount] = useState('');
  const [sourceModule, setSourceModule] = useState('manual');
  const [sourceDocRef, setSourceDocRef] = useState('');
  const [category, setCategory] = useState('materials');
  const [priority, setPriority] = useState('normal');
  const [duePayDate, setDuePayDate] = useState('');
  const [initiatorName, setInitiatorName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [vatError, setVatError] = useState<string | null>(null);

  // Ставка НДС на дату оплаты — из справочника ставок (с 2026 года 22 %), а не зашитая
  const vatRatePct = useMemo(() => {
    const onDate = duePayDate || todayIso();
    const points = (vatRates?.items ?? [])
      .filter((p) => p.effective_date <= onDate)
      .sort((a, b) => b.effective_date.localeCompare(a.effective_date));
    return points[0]?.rate_pct ?? null;
  }, [vatRates, duePayDate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isLoading) return;
    setErrorMsg('');

    const numAmount = parseMoney(amount);
    const numVat = vatAmount.trim() ? parseMoney(vatAmount) : undefined;
    const nextAmountError =
      numAmount === null
        ? 'Введите сумму к оплате, например 500 000,00'
        : numAmount <= 0
          ? 'Сумма к оплате должна быть больше нуля'
          : null;
    const nextVatError =
      numVat === null
        ? 'Введите сумму НДС числом или нажмите «Без НДС»'
        : numVat !== undefined && numAmount !== null && numVat > numAmount
          ? 'НДС не может быть больше суммы к оплате'
          : null;
    setAmountError(nextAmountError);
    setVatError(nextVatError);
    if (numAmount === null || nextAmountError || nextVatError) return;
    if (!counterpartyName.trim()) {
      setErrorMsg('Укажите наименование контрагента');
      return;
    }
    if (!purpose.trim()) {
      setErrorMsg('Укажите назначение платежа');
      return;
    }

    try {
      await createRequest({
        registry_id: activeRegistryId || undefined,
        counterparty_id: selectedCp ? selectedCp.id : undefined,
        counterparty_name: counterpartyName.trim(),
        counterparty_inn: counterpartyInn.trim() || undefined,
        amount: numAmount,
        vat_amount: numVat ?? undefined,
        source_module: sourceModule,
        source_doc_ref: sourceDocRef.trim() || undefined,
        category,
        priority,
        due_pay_date: duePayDate || undefined,
        initiator_name: initiatorName.trim() || 'Финансовая служба',
        purpose: purpose.trim(),
      }).unwrap();

      onClose();
      // reset
      setSelectedCpId('');
      setCpSearch('');
      setCounterpartyName('');
      setCounterpartyInn('');
      setAmount('');
      setVatAmount('');
      setAmountError(null);
      setVatError(null);
      setSourceDocRef('');
      setPurpose('');
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      setErrorMsg(apiErrorText(err, 'Не удалось создать заявку'));
    }
  }

  const modalContentRef = useRef<HTMLDivElement>(null);
  const cpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        cpInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!open) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        ref={modalContentRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-payment-request-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="create-payment-request-modal-title">Новая заявка на оплату</ModalTitle>
          <Button $variant="ghost" $size="sm" onClick={onClose} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          {activeRegistryId && (
            <ActiveRegistryBanner>
              <IconPaperclip size={14} />
              Заявка будет автоматически включена в активный реестр
            </ActiveRegistryBanner>
          )}

          {errorMsg && (
            <ErrorAlert>
              {errorMsg}
            </ErrorAlert>
          )}

          <DirectoryBox>
            <Label htmlFor="create-req-directory">Получатель из справочника контрагентов</Label>
            {directory.length > 0 && (
              <Input
                aria-label="Поиск получателя в справочнике по наименованию или ИНН"
                placeholder="Найти по наименованию или ИНН"
                value={cpSearch}
                onChange={(e) => setCpSearch(e.target.value)}
              />
            )}
            <Select
              id="create-req-directory"
              value={selectedCpId}
              onChange={(e) => selectCounterparty(e.target.value)}
            >
              <option value="">— Не из справочника, ввести вручную —</option>
              {selectedCp && !directoryOptions.some((cp) => cp.id === selectedCp.id) && (
                <option value={selectedCp.id}>{selectedCp.name}</option>
              )}
              {directoryOptions.map((cp) => (
                <option key={cp.id} value={cp.id}>
                  {cp.name} · ИНН {cp.inn}
                  {cp.kpp ? ` / КПП ${cp.kpp}` : ''}
                  {hasBankRequisites(cp) ? '' : ' · нет реквизитов'}
                </option>
              ))}
            </Select>
            {selectedCp ? (
              hasBankRequisites(selectedCp) ? (
                <RequisitesLine>
                  Р/с {selectedCp.account_number} · БИК {selectedCp.bik}
                  {selectedCp.bank_name ? ` · ${selectedCp.bank_name}` : ''}
                </RequisitesLine>
              ) : (
                <RequisitesLine $warn>
                  У контрагента нет расчётного счёта или БИК: заявка не попадёт в файл для банка,
                  пока их не дополнят в «Контрагенты и 1С».
                </RequisitesLine>
              )
            ) : (
              <RequisitesLine>
                Если ИНН найдётся в справочнике, реквизиты получателя подставятся сами. Без
                реквизитов заявка в файл для банка не попадёт.
              </RequisitesLine>
            )}
          </DirectoryBox>

          <Row>
            <FormGroup>
              <Label htmlFor="create-req-cp">Контрагент (Получатель) *</Label>
              <Input
                ref={cpInputRef}
                id="create-req-cp"
                required
                placeholder="ООО ЭнергоКомплект"
                value={counterpartyName}
                onChange={(e) => setCounterpartyName(e.target.value)}
              />
            </FormGroup>
          <FormGroup>
            <Label htmlFor="create-req-inn">ИНН контрагента</Label>
            <Input
              id="create-req-inn"
              placeholder="10 или 12 цифр"
              inputMode="numeric"
              value={counterpartyInn}
              onChange={(e) => {
                setCounterpartyInn(e.target.value);
                // ИНН поменяли вручную — выбранная карточка справочника больше не подходит
                if (selectedCp && e.target.value.trim() !== selectedCp.inn) {
                  setSelectedCpId('');
                }
              }}
            />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="create-req-amount">Сумма к оплате, ₽ *</Label>
            <Input
              id="create-req-amount"
              type="text"
              inputMode="decimal"
              required
              placeholder="500 000,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-invalid={Boolean(amountError)}
              aria-describedby={amountError ? 'create-req-amount-error' : undefined}
            />
            {amountError && <FieldError id="create-req-amount-error">{amountError}</FieldError>}
          </FormGroup>
          <FormGroup>
            <VatLabelRow>
              <Label htmlFor="create-req-vat">В т.ч. НДС, ₽</Label>
              <VatButtons>
                {vatRatePct !== null && (
                  <VatToggleBtn
                    type="button"
                    onClick={() => {
                      const num = parseMoney(amount);
                      if (num !== null && num > 0) {
                        const vat = (num * vatRatePct) / (100 + vatRatePct);
                        setVatAmount(moneyInput(Math.round(vat * 100) / 100));
                        setVatError(null);
                      }
                    }}
                  >
                    В т.ч. НДС {vatRatePct}%
                  </VatToggleBtn>
                )}
                <NoVatToggleBtn
                  type="button"
                  onClick={() => {
                    setVatAmount(moneyInput(0));
                    setVatError(null);
                  }}
                >
                  Без НДС
                </NoVatToggleBtn>
              </VatButtons>
            </VatLabelRow>
            <Input
              id="create-req-vat"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={vatAmount}
              onChange={(e) => setVatAmount(e.target.value)}
              aria-invalid={Boolean(vatError)}
              aria-describedby={vatError ? 'create-req-vat-error' : undefined}
            />
            {vatError && <FieldError id="create-req-vat-error">{vatError}</FieldError>}
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="create-req-source">Модуль-источник</Label>
            <Select
              id="create-req-source"
              value={sourceModule}
              onChange={(e) => setSourceModule(e.target.value)}
            >
              <option value="manual">Ручной ввод казначея</option>
              <option value="mto">МТО / Снабжение</option>
              <option value="logist">Логистика</option>
              <option value="smeta">Сметчик (Субподряд КС-3)</option>
              <option value="chronum_payroll">Хронум (ФОТ/Зарплата)</option>
              <option value="taxes_ens">Налоги (ЕНС/НДС)</option>
              <option value="debt_leasing">Кредиты и лизинг</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="create-req-doc">Первичный документ (Счёт/УПД)</Label>
            <Input
              id="create-req-doc"
              placeholder="Счёт № 142 от 15.10.2026"
              value={sourceDocRef}
              onChange={(e) => setSourceDocRef(e.target.value)}
            />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="create-req-category">Категория статьи БДДС</Label>
            <Select
              id="create-req-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="materials">МТО и кабельная продукция</option>
              <option value="fuel">ГСМ (топливо и масла)</option>
              <option value="subcontract">Субподрядные работы (СМР)</option>
              <option value="payroll">ФОТ, вахтовые и суточные</option>
              <option value="taxes">Налоги, НДС и ЕНС</option>
              <option value="debt_service">Погашение кредитов и процентов</option>
              <option value="leasing">Лизинговые платежи (техника)</option>
              <option value="equipment">Силовое оборудование и РЗА</option>
              <option value="overhead">Накладные и общехозяйственные</option>
              <option value="other">Прочие платежи</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="create-req-priority">Приоритет</Label>
            <Select
              id="create-req-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="critical">Критичный (налоги / кредиты / штрафы)</option>
              <option value="high">Высокий (критичный путь СМР)</option>
              <option value="normal">Плановый (по графику)</option>
              <option value="low">Низкий (допустимо перенести)</option>
            </Select>
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="create-req-due">Желаемый срок оплаты</Label>
            <Input
              id="create-req-due"
              type="date"
              value={duePayDate}
              onChange={(e) => setDuePayDate(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="create-req-initiator">Инициатор / Служба</Label>
            <Input
              id="create-req-initiator"
              placeholder="Отдел снабжения"
              value={initiatorName}
              onChange={(e) => setInitiatorName(e.target.value)}
            />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label htmlFor="create-req-purpose">Назначение платежа *</Label>
          <Textarea
            id="create-req-purpose"
            required
            placeholder="Оплата за поставку силового кабеля ВВГнг-LS по счету № 142..."
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          />
        </FormGroup>

        <ActionsRow>
          <Button $variant="secondary" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button $variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Создание…' : 'Создать заявку'}
          </Button>
        </ActionsRow>
      </Form>
    </ModalContent>
  </ModalOverlay>
);
}
